import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import {
  terminalCommands,
  getCommandResponse,
  getHelpText,
  findCommand,
  getCommandSuggestions,
  requiresConfirmation,
  getConfirmationMessage,
} from "../../../config/terminalCommands";

const TerminalNavigation = () => {
  const [input, setInput] = useState("");
  const [isTerminalConnected, setIsTerminalConnected] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [commandHistory, setCommandHistory] = useState([]);
  const [outputHistory, setOutputHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const terminalContentRef = useRef(null);
  const navigate = useNavigate();

  // Activate terminal after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTerminalConnected(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll to bottom when new output is added
  useEffect(() => {
    if (terminalContentRef.current) {
      terminalContentRef.current.scrollTop = terminalContentRef.current.scrollHeight;
    }
  }, [outputHistory]);

  useEffect(() => {
    setSuggestions(getCommandSuggestions(input, 5));
  }, [input]);

  const handleCommand = (command) => {
    const cmd = findCommand(command);
    
    // Add command to output history
    const newOutput = {
      command,
      timestamp: new Date().toLocaleTimeString(),
      output: null,
      type: 'command'
    };

    setCommandHistory((prev) => [...prev, command]);
    setHistoryIndex(-1);

    // Handle special cases first
    if (command === "help") {
      newOutput.output = getHelpText();
      setOutputHistory((prev) => [...prev, newOutput]);
      setInput("");
      setSuggestions([]);
      return;
    }

    if (command === "clear") {
      setCommandHistory([]);
      setOutputHistory([]);
      setInput("");
      setSuggestions([]);
      return;
    }

    // Command not found
    if (!cmd) {
      newOutput.output = `Command not found: ${command}. Type 'help' for available commands.`;
      newOutput.type = 'error';
      setOutputHistory((prev) => [...prev, newOutput]);
      setInput("");
      setSuggestions([]);
      return;
    }

    // Check if command requires confirmation
    if (requiresConfirmation(cmd)) {
      const confirmMsg = getConfirmationMessage(cmd);
      newOutput.output = `${confirmMsg} (Y/N)`;
      newOutput.type = 'confirmation';
      setOutputHistory((prev) => [...prev, newOutput]);
      setPendingConfirmation({ command: cmd, originalCommand: command });
      setInput("");
      setSuggestions([]);
      return;
    }

    // Execute command based on action type
    executeCommand(cmd, newOutput);
  };

  const executeCommand = (cmd, outputEntry) => {
    const response = getCommandResponse(cmd);

    switch (cmd.action) {
      case 'navigate':
        outputEntry.output = response || `Navigating to ${cmd.description}...`;
        setOutputHistory((prev) => [...prev, outputEntry]);
        
        // Show "Redirecting..." message
        setTimeout(() => {
          const redirectOutput = {
            command: null,
            timestamp: new Date().toLocaleTimeString(),
            output: `Redirecting in 3 seconds...`,
            type: 'info'
          };
          setOutputHistory((prev) => [...prev, redirectOutput]);
        }, 500);

        // Handle external URLs vs internal routes
        setTimeout(() => {
          if (cmd.external || cmd.route?.startsWith('http://') || cmd.route?.startsWith('https://')) {
            window.open(cmd.route, '_blank');
          } else {
            navigate(cmd.route);
          }
        }, 3000);
        break;

      case 'show-response':
        outputEntry.output = response;
        setOutputHistory((prev) => [...prev, outputEntry]);
        break;

      case 'easter-egg':
        outputEntry.output = response;
        outputEntry.type = 'easter-egg';
        setOutputHistory((prev) => [...prev, outputEntry]);
        break;

      default:
        outputEntry.output = response || "Command executed successfully.";
        setOutputHistory((prev) => [...prev, outputEntry]);
    }

    setInput("");
    setSuggestions([]);
  };

  const handleConfirmation = (confirmed) => {
    if (!pendingConfirmation) return;

    const { command, originalCommand } = pendingConfirmation;
    
    const confirmOutput = {
      command: confirmed ? 'Y' : 'N',
      timestamp: new Date().toLocaleTimeString(),
      output: null,
      type: 'command'
    };

    if (confirmed) {
      confirmOutput.output = "Confirmed.";
      setOutputHistory((prev) => [...prev, confirmOutput]);
      
      // Execute the pending command
      const newOutput = {
        command: originalCommand,
        timestamp: new Date().toLocaleTimeString(),
        output: null,
        type: 'command'
      };
      executeCommand(command, newOutput);
    } else {
      confirmOutput.output = "Cancelled.";
      setOutputHistory((prev) => [...prev, confirmOutput]);
    }

    setPendingConfirmation(null);
    setInput("");
  };

  const handleKeyPress = (e) => {
    if (e?.key === "Enter") {
      if (input?.trim()) {
        // If waiting for confirmation, handle Y/N
        if (pendingConfirmation) {
          const response = input.trim().toUpperCase();
          if (response === 'Y' || response === 'YES') {
            handleConfirmation(true);
          } else if (response === 'N' || response === 'NO') {
            handleConfirmation(false);
          } else {
            const errorOutput = {
              command: input,
              timestamp: new Date().toLocaleTimeString(),
              output: "Please enter Y (yes) or N (no)",
              type: 'error'
            };
            setOutputHistory((prev) => [...prev, errorOutput]);
            setInput("");
          }
        } else {
          handleCommand(input?.trim());
        }
      }
    } else if (e?.key === "Tab") {
      e?.preventDefault();
      if (suggestions?.length > 0 && !pendingConfirmation) {
        setInput(suggestions?.[0]?.cmd);
        setSuggestions([]);
      }
    } else if (e?.key === "ArrowUp") {
      e?.preventDefault();
      if (
        !pendingConfirmation &&
        commandHistory?.length > 0 &&
        historyIndex < commandHistory?.length - 1
      ) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory?.[commandHistory?.length - 1 - newIndex]);
      }
    } else if (e?.key === "ArrowDown") {
      e?.preventDefault();
      if (!pendingConfirmation) {
        if (historyIndex > 0) {
          const newIndex = historyIndex - 1;
          setHistoryIndex(newIndex);
          setInput(commandHistory?.[commandHistory?.length - 1 - newIndex]);
        } else if (historyIndex === 0) {
          setHistoryIndex(-1);
          setInput("");
        }
      }
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-card border border-terminal rounded-lg terminal-glow h-[600px] flex flex-col">
        {/* Fixed Terminal Header */}
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-destructive rounded-full"></div>
            <div className="w-3 h-3 bg-warning rounded-full"></div>
            <div className="w-3 h-3 bg-success rounded-full"></div>
          </div>

          <div className="flex items-center space-x-2 text-muted-foreground text-xs">
            <div
              className={`w-2 h-2 ${isTerminalConnected ? "bg-success" : "bg-warning"
                } rounded-full animate-pulse`}
            ></div>
            <Icon name="Wifi" size={12} />
            <span>{isTerminalConnected ? "Connected" : "Connecting..."}</span>
          </div>
        </div>

        {/* Scrollable Terminal Content */}
        <div 
          ref={terminalContentRef}
          className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-terminal/50 scrollbar-track-transparent"
        >
          {/* Welcome Message - Fixed at top */}
          <div className="sticky top-0 bg-card z-10 p-6 pb-4 border-b border-border/50">
            <div className="text-terminal-cyan text-sm font-fira mb-2">
              Welcome to DeveloperNoob Terminal v2.1.4
            </div>
            <div className="text-muted-foreground text-xs font-source mb-2">
              Type 'help' for available commands or start typing to see suggestions
            </div>
            <div className="text-muted-foreground text-xs font-source">
              Last login: {new Date().toLocaleString()} on ttys001
            </div>
          </div>

          {/* Scrollable Command Area */}
          <div className="p-6 pt-4">
          {/* Command History & Output */}
          {outputHistory?.map((entry, index) => (
            <div key={index} className="mb-3">
              {/* Command Line */}
              {entry.command !== null && (
                <div className="flex items-center text-sm font-fira mb-1">
                  <span className="text-terminal-cyan mr-1">developer@noob</span>
                  <span className="text-terminal-magenta mr-1">:</span>
                  <span className="text-terminal-blue mr-2">~</span>
                  <span className="text-terminal-cyan mr-2">$</span>
                  <span className="text-foreground">{entry.command}</span>
                </div>
              )}
              
              {/* Command Output */}
              {entry.output && (
                <div className={`font-fira text-xs whitespace-pre-wrap mb-2 ${
                  entry.type === 'error' ? 'text-destructive' : 
                  entry.type === 'confirmation' ? 'text-warning' :
                  entry.type === 'easter-egg' ? 'text-terminal-cyan' :
                  entry.type === 'info' ? 'text-terminal-magenta animate-pulse' :
                  'text-muted-foreground'
                }`}>
                  {entry.output}
                </div>
              )}
            </div>
          ))}

          {/* Current Input Line */}
          <div className="text-sm font-fira flex items-center">
            <span className="text-terminal-cyan mr-1">developer@noob</span>
            <span className="text-terminal-magenta mr-1">:</span>
            <span className="text-terminal-blue mr-2">~</span>
            <span className="text-terminal-cyan mr-2">$</span>
            <div className="flex-1 relative">
              <input
                type="text"
                value={input}
                disabled={!isTerminalConnected}
                onChange={(e) => setInput(e?.target?.value)}
                onKeyDown={handleKeyPress}
                className="w-full bg-transparent text-foreground font-fira text-sm outline-none caret-terminal-cyan"
                placeholder={pendingConfirmation ? "(Y/N)" : ""}
                autoFocus
              />
            </div>
          </div>

          {/* Suggestions - Only show when not waiting for confirmation */}
          {!pendingConfirmation && suggestions?.length > 0 && (
            <div className=" mt-4 bg-muted/95 backdrop-blur-sm border border-border rounded p-3 shadow-2xl">
              <div className="text-xs text-muted-foreground mb-2 font-source">
                Suggestions (press Tab to autocomplete):
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-terminal/50 scrollbar-track-transparent">
                {suggestions?.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleCommand(suggestion?.cmd)}
                    className="flex items-center justify-between w-full text-left p-2 rounded hover:bg-muted/50 transition-colors duration-200"
                  >
                    <span className="text-primary font-fira text-sm">
                      {suggestion?.cmd}
                    </span>
                    <span className="text-muted-foreground text-xs font-source">
                      {suggestion?.description}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Confirmation Quick Actions */}
          {pendingConfirmation && (
            <div className="mt-4 bg-muted/30 border border-warning/50 rounded p-3">
              <div className="text-xs text-warning mb-2 font-source">
                Awaiting confirmation:
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleConfirmation(true)}
                  className="flex-1 bg-success/20 hover:bg-success/30 text-success border border-success/50 rounded px-3 py-2 text-sm font-fira transition-colors duration-200"
                >
                  Yes (Y)
                </button>
                <button
                  onClick={() => handleConfirmation(false)}
                  className="flex-1 bg-destructive/20 hover:bg-destructive/30 text-destructive border border-destructive/50 rounded px-3 py-2 text-sm font-fira transition-colors duration-200"
                >
                  No (N)
                </button>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Fixed Footer Help Text */}
        <div className="border-t border-border px-6 py-2 text-xs text-muted-foreground font-source flex-shrink-0">
          Pro tip: Use ↑/↓ arrows for command history, Tab for autocomplete
        </div>
      </div>
    </div>
  );
};

export default TerminalNavigation;
